// Invoice exports: a sendable payment-request PDF and a CSV of line items.
// Pricing comes from effectiveDrawPrice (per-draw invoice_price, default $23).
import jsPDF from 'jspdf';
import { effectiveDrawPrice } from './invoicePricing';

const BRAND = 'AtHomeBloodDraw.com';
const REMIT_LINES = [
  'Payment due upon receipt.',
  'Pay by ACH to:  At Home Blood Draw Inc',
  'Routing #: 101019644      Account #: 214141605740',
  'Or reply to this invoice to request a card payment link.',
];

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (d) => {
  if (!d) return '';
  try { const x = new Date(d); return isNaN(x) ? '' : x.toLocaleDateString('en-US'); } catch { return ''; }
};

function lineItems(subs) {
  return (subs || []).map((s) => ({
    patient: s.patient_name || `#${s.id}`,
    drawDate: fmtDate(s.blood_collection_time),
    drawCode: s.draw_code || '',
    price: effectiveDrawPrice(s),
  }));
}

const csvCell = (v) => {
  const s = (v == null ? '' : String(v));
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Build a CSV string for an invoice's line items (+ total row). */
export function buildInvoiceCSV({ invoice, clientName, subs }) {
  const items = lineItems(subs);
  const total = items.reduce((sum, i) => sum + i.price, 0);
  const rows = [
    ['Invoice', invoice?.invoice_number || ''],
    ['Client', clientName || ''],
    ['Date', fmtDate(invoice?.created_at || invoice?.invoice_generated_at)],
    [],
    ['Patient', 'Draw Date', 'Draw Code', 'Price'],
    ...items.map((i) => [i.patient, i.drawDate, i.drawCode, i.price.toFixed(2)]),
    [],
    ['', '', 'Total', total.toFixed(2)],
    [],
    ['Payment due upon receipt'],
    ['Pay by ACH to', 'At Home Blood Draw Inc'],
    ['Routing #', '101019644'],
    ['Account #', '214141605740'],
  ];
  return rows.map((r) => r.map(csvCell).join(',')).join('\n');
}

/** Trigger a browser download of arbitrary text content. */
export function downloadText(filename, text, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Build and download a payment-request PDF for an invoice. */
export function downloadInvoicePDF({ invoice, clientName, subs }) {
  const items = lineItems(subs);
  const total = items.reduce((sum, i) => sum + i.price, 0);
  const doc = new jsPDF();
  const left = 14;
  const right = 196;
  let y = 18;

  doc.setFontSize(20); doc.setFont(undefined, 'bold');
  doc.text(BRAND, left, y);
  doc.setFontSize(14); doc.setTextColor(120);
  doc.text('Payment Request', right, y, { align: 'right' });
  doc.setTextColor(0);

  y += 10; doc.setFontSize(10); doc.setFont(undefined, 'normal');
  doc.text(`Invoice #: ${invoice?.invoice_number || '—'}`, left, y);
  doc.text(`Date: ${fmtDate(invoice?.created_at || invoice?.invoice_generated_at) || fmtDate(new Date())}`, right, y, { align: 'right' });
  y += 6;
  doc.setFont(undefined, 'bold'); doc.text('Bill To:', left, y);
  doc.setFont(undefined, 'normal'); doc.text(clientName || '—', left + 16, y);

  // Table header
  y += 12;
  doc.setFillColor(244, 244, 248); doc.rect(left, y - 5, right - left, 8, 'F');
  doc.setFont(undefined, 'bold'); doc.setFontSize(9);
  doc.text('Patient', left + 2, y);
  doc.text('Draw Date', left + 86, y);
  doc.text('Draw Code', left + 124, y);
  doc.text('Price', right - 2, y, { align: 'right' });
  doc.setFont(undefined, 'normal');

  y += 7;
  items.forEach((i) => {
    if (y > 272) { doc.addPage(); y = 20; }
    doc.text(String(i.patient).slice(0, 44), left + 2, y);
    doc.text(i.drawDate, left + 86, y);
    doc.text(String(i.drawCode), left + 124, y);
    doc.text(money(i.price), right - 2, y, { align: 'right' });
    y += 6;
  });

  // Total
  y += 2; doc.setDrawColor(200); doc.line(left, y, right, y); y += 7;
  doc.setFont(undefined, 'bold'); doc.setFontSize(11);
  doc.text(`Total Due: ${money(total)}`, right - 2, y, { align: 'right' });
  doc.text(`${items.length} draw${items.length === 1 ? '' : 's'}`, left + 2, y);

  // Remittance
  y += 12; doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(90);
  REMIT_LINES.forEach((ln) => { if (y > 280) { doc.addPage(); y = 20; } doc.text(ln, left, y); y += 5; });

  doc.save(`invoice-${invoice?.invoice_number || 'export'}.pdf`);
}
